import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import AppHeader from '@/components/AppHeader';
import { getBroadcastSettings, setActiveBroadcast, setDefaultTournament, getAllBroadcastIds, getTournaments } from '@/lib/supabase';

interface BroadcastMatch {
  broadcastId: string;
  matchNumber: number;
  teamA: string;
  teamB: string;
  status: string;
  tournamentId: string | null;
}
export default function BroadcastControl() {
  const navigate = useNavigate();
  const { profile, isAdmin, signOut } = useAuth();
  const [matches, setMatches] = useState<BroadcastMatch[]>([]);
  const [selectedBroadcastId, setSelectedBroadcastId] = useState<string>('');
  const [currentLiveId, setCurrentLiveId] = useState<string | null>(null);
  const [tournaments, setTournaments] = useState<{ id: string; name: string }[]>([]);
  const [selectedDefaultTournamentId, setSelectedDefaultTournamentId] = useState<string>('');
  const [currentDefaultTournamentId, setCurrentDefaultTournamentId] = useState<string | null>(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [broadcastIds, settings, tournamentsData] = await Promise.all([
        getAllBroadcastIds(),
        getBroadcastSettings(),
        getTournaments(),
      ]);
      setMatches(broadcastIds);
      setTournaments(tournamentsData.map(t => ({ id: t.id, name: t.name })));
      setCurrentLiveId(settings?.activeBroadcastId || null);
      setCurrentDefaultTournamentId(settings?.defaultTournamentId || null);
      if (!selectedBroadcastId && broadcastIds.length > 0) {
        setSelectedBroadcastId(settings?.activeBroadcastId || broadcastIds[0].broadcastId);
      }
      if (!selectedDefaultTournamentId) {
        setSelectedDefaultTournamentId(settings?.defaultTournamentId || '');
      }
    } catch {
      setError('Failed to load broadcast data');
    } finally {
      setLoading(false);
    }
  };

  const handleGoLive = async () => {
    if (!selectedBroadcastId) {
      setError('Please select a broadcast ID');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await setActiveBroadcast(selectedBroadcastId, profile?.id);
      setCurrentLiveId(selectedBroadcastId);
      setSuccess(`Now live: ${selectedBroadcastId}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to update broadcast');
    } finally {
      setSaving(false);
    }
  };

  const handleStopBroadcast = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await setActiveBroadcast(null, profile?.id);
      setCurrentLiveId(null);
      setSuccess('Broadcast stopped');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to stop broadcast');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDefaultTournament = async () => {
    setSavingDefault(true);
    setError(null);
    setSuccess(null);
    try {
      await setDefaultTournament(selectedDefaultTournamentId || null);
      setCurrentDefaultTournamentId(selectedDefaultTournamentId || null);
      setSuccess('Default tournament saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to save default tournament');
    } finally {
      setSavingDefault(false);
    }
  };

  const getMatchLabel = (m: BroadcastMatch) => {
    const tournamentName = m.tournamentId
      ? (tournaments.find(t => t.id === m.tournamentId)?.name ?? '')
      : '';
    const tournamentPart = tournamentName ? ` (${tournamentName})` : '';
    return `${m.broadcastId} - Match ${m.matchNumber}${tournamentPart}: ${m.teamA} vs ${m.teamB}`;
  };

  const currentLiveMatch = matches.find(m => m.broadcastId === currentLiveId);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader onSignOut={signOut} title="Broadcast Control" navIcon="back" onNavigate={() => navigate('/admin')} />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-text-muted">Admin access required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader onSignOut={signOut} title="Broadcast Control" navIcon="back" onNavigate={() => navigate('/admin')} />

      <div className="flex-1 overflow-y-auto px-4 py-6 safe-area-bottom max-w-md mx-auto w-full">
        {/* Current Live Status */}
        <div className="card mb-4 bg-surface-raised">
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
            Current Live Broadcast
          </p>
          {currentLiveId ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-success animate-pulse" />
                <span className="text-lg font-bold text-success">{currentLiveId}</span>
              </div>
              {currentLiveMatch && (
                <p className="text-sm text-text-secondary">
                  Match {currentLiveMatch.matchNumber}: {currentLiveMatch.teamA} vs {currentLiveMatch.teamB}
                </p>
              )}
              <p className="text-xs text-text-muted">
                Overlay URL: <span className="font-mono text-text-primary">/overlay/live</span>
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-text-muted" />
              <span className="text-text-muted">No broadcast is live</span>
            </div>
          )}
        </div>

        {/* Broadcast Selection */}
        <div className="card mb-4">
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
            Select Broadcast to Go Live
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-6 h-6 border-2 border-serving border-t-transparent rounded-full animate-spin" />
            </div>
          ) : matches.length === 0 ? (
            <p className="text-text-muted text-sm text-center py-4">No matches available</p>
          ) : (
            <div className="space-y-3">
              <select
                value={selectedBroadcastId}
                onChange={(e) => setSelectedBroadcastId(e.target.value)}
                className="w-full input-field text-sm"
              >
                {matches.map((m) => (
                  <option key={m.broadcastId} value={m.broadcastId}>
                    {getMatchLabel(m)}
                  </option>
                ))}
              </select>

              <button
                onClick={handleGoLive}
                disabled={saving || !selectedBroadcastId}
                className="w-full btn-primary bg-success text-white font-bold disabled:opacity-50"
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    UPDATING...
                  </span>
                ) : (
                  'GO LIVE'
                )}
              </button>
            </div>
          )}
        </div>

        {/* Stop Broadcast */}
        {currentLiveId && (
          <button
            onClick={handleStopBroadcast}
            disabled={saving}
            className="w-full btn-secondary text-error border-error/50 hover:border-error font-bold mb-4"
          >
            STOP BROADCAST
          </button>
        )}

        {/* Default Tournament for /live page */}
        <div className="card mb-4">
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">
            Default Tournament — Public /live Page
          </p>
          {currentDefaultTournamentId && (
            <p className="text-xs text-text-secondary mb-3">
              Current: <span className="font-semibold text-text-primary">
                {tournaments.find(t => t.id === currentDefaultTournamentId)?.name ?? currentDefaultTournamentId}
              </span>
            </p>
          )}
          <select
            value={selectedDefaultTournamentId}
            onChange={e => setSelectedDefaultTournamentId(e.target.value)}
            className="w-full input-field text-sm mb-3"
          >
            <option value="">— Auto-select by date —</option>
            {tournaments.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <button
            onClick={handleSaveDefaultTournament}
            disabled={savingDefault}
            className="w-full btn-primary font-bold disabled:opacity-50"
          >
            {savingDefault ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                SAVING...
              </span>
            ) : 'SAVE DEFAULT TOURNAMENT'}
          </button>
          <p className="text-xs text-text-muted mt-2">
            Viewers opening <span className="font-mono">/live</span> without a direct link will see this tournament first. They can still switch tournaments.
          </p>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="card bg-error/10 border-error/30 mb-4">
            <p className="text-error text-sm">{error}</p>
          </div>
        )}
        {success && (
          <div className="card bg-success/10 border-success/30 mb-4">
            <p className="text-success text-sm font-medium">{success}</p>
          </div>
        )}

        {/* Instructions */}
        <div className="card bg-surface-raised">
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
            How It Works
          </p>
          <ol className="text-sm text-text-secondary space-y-2 list-decimal list-inside">
            <li>Select a broadcast ID from the dropdown</li>
            <li>Click <span className="font-semibold text-text-primary">GO LIVE</span></li>
            <li>The overlay at <span className="font-mono text-text-primary">/overlay/live</span> will update</li>
            <li>Use this single URL in your streaming software</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
